import { detach, Instance, SnapshotOut, types } from "mobx-state-tree"
import { Toast } from "native-base";
import { Platform } from "react-native";
import { AutoFeederPond, Cycle, CycleActual, CyclePrediction } from "../../../../database"
import { CycleStoreModel } from "./cycle-store/cycle-store";
import { AutoFeederPondStoreModel } from "./auto-feeder-pond-store/auto-feeder-pond-store"
import { IAnyStateTreeNode } from "mobx-state-tree/dist/internal"
import moment from "moment"
import _ from "lodash"

const PondStoreProps = {
  pondId: types.maybeNull(types.number),
  pond: types.maybeNull(types.frozen()),
  cycleId: types.maybeNull(types.number),
  cycleStoreModels: types.optional(types.array(CycleStoreModel), []),
  autoFeederPondId: types.maybeNull(types.number),
  autoFeederPondStoreModels: types.optional(types.array(AutoFeederPondStoreModel), []),

  queryingCycle: types.maybeNull(types.boolean),
  lastUpdateCycle: types.maybeNull(types.string),
  syncingCycle: types.maybeNull(types.boolean),
  syncedCycle: types.maybeNull(types.boolean),
  queryingCycleActual: types.maybeNull(types.boolean),
  syncingCycleActual: types.maybeNull(types.boolean),
  syncedCycleActual: types.maybeNull(types.boolean),
  queryingCyclePrediction: types.maybeNull(types.boolean),
  syncingCyclePrediction: types.maybeNull(types.boolean),
  syncedCyclePrediction: types.maybeNull(types.boolean),
  queryingAutoFeederPond: types.maybeNull(types.boolean),
  syncingAutoFeederPond: types.maybeNull(types.boolean),
  syncedAutoFeederPond: types.maybeNull(types.boolean)
}

/**
 * Model description here for TypeScript hints.
 */
export const PondStoreModel = types
  .model("PondStore")
  .props(PondStoreProps)
  .views((self) => ({
    get cycleStore() {
      return self.cycleStoreModels.find((cycleStore) => cycleStore.cycleId === self.cycleId)
    },
    get cycles() {
      return self.cycleStoreModels.map((cycleStore) => cycleStore.cycle)
    },
    get cycle() {
      return (this.cycleStore || {}).cycle
    },
    get autoFeederPondStore() {
      return self.autoFeederPondStoreModels.find((autoFeederPond) => autoFeederPond.autoFeederPondId === self.autoFeederPondId)
    },
    get autoFeederPonds() {
      return self.autoFeederPondStoreModels.map((autoFeederPond) => autoFeederPond.autoFeederPond)
    },
    get autoFeederPond() {
      return (this.autoFeederPondStore || {}).autoFeederPond
    },
    get activeCycleStoreModel() {
      return self.cycleStoreModels.find((cycleStore) => !cycleStore.cycle.finished_at)
    },
    get autoFeederPondTotalFeedToday() {
      return self.autoFeederPondStoreModels.reduce((previousValue, currentValue) => (
        previousValue + (currentValue.feedToday?.quantity || 0)
      ), 0)
    },
    get autoFeederPondTotalFeedTomorrow() {
      return self.autoFeederPondStoreModels.reduce((previousValue, currentValue) => (
        previousValue + (currentValue.feedTomorrow?.quantity || 0)
      ), 0)
    },
    get activeCycle() {
      return (this.activeCycleStoreModel || {}).cycle
    },
    get lastCycleStore() {
      return self.cycleStoreModels.find((cycleStoreModel) => cycleStoreModel)
    },
    get lastCycle() {
      return (this.lastCycleStore || {}).cycle
    },
    get todayCycleStoreModel() {
      return self.cycleStoreModels.find((cycleStore) => (
        cycleStore.cycle.started_at <= moment().format('YYYY-MM-DD')
        && (!cycleStore.cycle.finished_at || cycleStore.cycle.finished_at >= moment().format('YYYY-MM-DD'))
      ))
    },
    get todayCycle() {
      return (this.todayCycleStoreModel || {}).cycle
    },
    get loadingCycle() {
      return self.queryingCycle || self.syncingCycle
    },
    get loadingAutoFeederPond() {
      return self.queryingAutoFeederPond || self.syncingAutoFeederPond
    },
    get loadingCyclePredictionCycleActuals() {
      return self.cycleStoreModels.map((cycleStore) => cycleStore.loadingCyclePredictionCycleActual)
    },
    get loadingCyclePredictionCycleActual() {
      return self.cycleStoreModels.reduce((previousValue, cycleStore) => previousValue || cycleStore.loadingCyclePredictionCycleActual, false)
    },
    get queryingCycleActualAndPrediction() {
      return self.queryingCycleActual || self.queryingCyclePrediction
    },
    get syncingCycleActualAndPrediction() {
      return self.syncingCycleActual || self.syncingCyclePrediction
    },
    get loadingCycleActualAndPrediction() {
      return this.queryingCycleActualAndPrediction || this.syncingCycleActualAndPrediction
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof PondStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    setArrayModelStore(
      key: "cycleStoreModels"|"autoFeederPondStoreModels",
      values: Array<any>,
      modelId: string, model: string, modelKey: string,
      defaultStore: (props?: any) => IAnyStateTreeNode
    ) {
      try {
        const modelStores = [...self[key]]
        this.set(key, values.map((value, index) => {
          const modelStoreIndex = modelStores.findIndex((modelStore) => modelStore[modelId] === value[modelKey]);
          let modelStore;
          if (modelStoreIndex < 0) modelStore = defaultStore();
          else if (index === modelStoreIndex) modelStore = modelStores[modelStoreIndex];
          else modelStore = detach(modelStores[modelStoreIndex]);
          modelStore.set(modelId, value[modelKey]);
          modelStore.set(model, value);
          return modelStore;
        }))
      } catch (e) {
        console.warn(e);
        this.set(key, []);
      }
    },
    async getCycles(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingCycle', true);
        const cycles = await Cycle.fetch({
          pond_id__eq: self.pondId,
          sort: 'started_at,desc|pond_name_alpha_numeric'
        }).catch((e) => {
          console.warn(e)
          return []
        })
        this.setArrayModelStore(
          'cycleStoreModels',
          cycles.map((c) => ({
            ...c.toArray(),
            pro: self.pond.pro || c.getAttribute('pro')
          })),
          'cycleId', 'cycle', 'id',
          (props = {}) => CycleStoreModel.create(props)
        )
        this.set('lastUpdateCycle', `${_.max(cycles.map((m: Cycle) => m.getAttributes().updated_at)) || ''}-${cycles.length}`)
        if (!cycles.find((cycle) => cycle.getKey() === self.cycleId)) {
          this.set('cycleId', cycles[0] && cycles[0].getKey() || null)
        }
        this.set('queryingCycle', false);
      } else {
        this.set('queryingCycle', true);
        this.syncCycles().then(async () => {
          await this.getCycles(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedCycle', true);
          this.set('queryingCycle', false);
        })
      }
    },
    async syncCycles() {
      if (Platform.OS === 'web') return true;

      this.set('syncingCycle', true)
      Cycle.endpoint = `/ponds/${self.pondId}/cycles`
      const success = await Cycle.syncAll(
        { pond_id__in: self.pondId },
        [['cycles.pond_id', [self.pondId]]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )
      Cycle.endpoint = `/cycles`

      this.set('syncingCycle', false)
      return success
    },
    async getAutoFeederPonds(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingAutoFeederPond', true);
        const autoFeederPonds = await AutoFeederPond.fetch({ pond_id__eq: self.pondId, sort: 'name,asc|id,asc' }).catch((e) => {
          console.warn(e)
          return []
        })
        this.setArrayModelStore(
          'autoFeederPondStoreModels',
          autoFeederPonds.map((a) => a.toArray()),
          'autoFeederPondId', 'autoFeederPond', 'id',
          (props = {}) => AutoFeederPondStoreModel.create(props)
        )
        if (!autoFeederPonds.find((autoFeederPond) => autoFeederPond.getKey() === self.autoFeederPondId)) {
          this.set('autoFeederPondId', autoFeederPonds[0] && autoFeederPonds[0].getKey() || null)
        }
        this.set('queryingAutoFeederPond', false);
      } else {
        this.set('queryingAutoFeederPond', true);
        this.syncAutoFeederPonds().then(async () => {
          await this.getAutoFeederPonds(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedAutoFeederPond', true);
          this.set('queryingAutoFeederPond', false);
        })
      }
    },
    async syncAutoFeederPonds() {
      if (Platform.OS === 'web') return true;

      this.set('syncingAutoFeederPond', true)
      const success = await AutoFeederPond.syncAll(
        { pond_id__in: self.pondId },
        [['auto_feeder_ponds.pond_id', [self.pondId]]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      ).catch(() => false)

      this.set('syncingAutoFeederPond', false)
      return success
    },
    async getCycleActualAndPredictions(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingCycleActual', true);
        this.set('queryingCyclePrediction', true);
        const cycles = self.cycles;
        const cycleIds = cycles.map((cycle) => cycle.id)
        const cycleAges = _.uniq(cycles.flatMap((cycle) => [...Array(5)].map((v, index) => (cycle.age + 1 - index))));
        const [ cyclePredictions, cycleActuals ] = await Promise.all([
          CyclePrediction.fetch({
            cycle_id__in: cycleIds.join(','),
            age__in: cycleAges.join(','),
            sort: 'date,desc',
            generate_by_cycle_id: cycleIds.join(','),
          }).catch((e) => {
            console.warn(e)
            return []
          }),
          CycleActual.fetch({
            cycle_id__in: cycleIds.join(','),
            age__in: cycleAges.join(','),
            sort: 'date,desc',
            generate_by_cycle_id: cycleIds.join(','),
          }).catch((e) => {
            console.warn(e)
            return []
          }),
        ])
        self.cycleStoreModels.forEach((cycleStore) => {
          cycleStore.set('cycleActuals', _.orderBy(_.uniqBy([
            ...cycleActuals.filter((c: CycleActual) => c.getAttribute('cycle_id') === cycleStore.cycleId).map((c: CycleActual) => c.toArray()),
            ...cycleStore.cycleActuals,
          ], 'age'), ['age'], ['desc']))
          cycleStore.set('cyclePredictions', _.orderBy((
            cyclePredictions.filter((c: CyclePrediction) => c.getAttribute('cycle_id') === cycleStore.cycleId).map((c: CyclePrediction) => c.toArray())
          ), ['age'], ['desc']))
        })
        this.set('lastUpdateCycle',  moment.utc().format())
        this.set('queryingCycleActual', false);
        this.set('queryingCyclePrediction', false);
      }
      if (sync) {
        this.set('queryingCycleActual', true);
        this.set('queryingCyclePrediction', true);
        this.syncCycleActualAndPredictions().then(async () => {
          await this.getCycleActualAndPredictions(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedCycleActual', true)
          this.set('syncedCyclePrediction', true)
          this.set('queryingCycleActual', false);
          this.set('queryingCyclePrediction', false);
        })
      }
    },
    async syncCycleActualAndPredictions() {
      if (Platform.OS === 'web') return true;

      const cycles = self.cycles
      const cycleIds = cycles.map((cycle) => cycle.id);
      const cycleAges = _.uniq(cycles.flatMap((cycle) => [...Array(5)].map((v, index) => (cycle.age + 1 - index))));
      return (await Promise.all([
        CycleActual.syncAll({
          cycle_id__in: cycleIds.join(','),
          age__in: cycleAges.join(','),
          generate_by_cycle_id: cycleIds.join(',')
        }, [
          ['cycle_actuals.cycle_id', cycleIds],
          ['cycle_actuals.age', cycleAges]
        ], false, (p) => {
          this.set('syncingCycleActual', p !== null && p !== 100)
        }, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }),
        CyclePrediction.syncAll({
          cycle_id__in: cycleIds.join(','),
          age__in: cycleAges.join(','),
          generate_by_cycle_id: cycleIds.join(',')
        }, [
          ['cycle_predictions.cycle_id', cycleIds],
          ['cycle_predictions.age', cycleAges]
        ], false, (p) => {
          this.set('syncingCyclePrediction', p !== null && p !== 100)
        }, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        })
      ])).reduce((previousValue, currentValue) => previousValue && currentValue, true)
    },
    init() {
      this.getCycles().catch()
    },
    initWithLastCycle() {
      this.getCycles(true, () => {
        if (self.lastCycleStore) self.lastCycleStore.init()
      }).catch()
    },
    getCycleStoreModelByDate(date) {
      return self.cycleStoreModels.find((cycleStore) => (
        cycleStore.cycle.started_at <= date && (!cycleStore.cycle.finished_at || (date <= cycleStore.cycle.finished_at))
      ))
    },
    getCycleStoreModelWithPreparationDayByDate(date) {
      return self.cycleStoreModels.find((cycleStore) => (
        moment(cycleStore.cycle.started_at).subtract(cycleStore.cycle.preparation_day || 21, 'days').format('YYYY-MM-DD') <= date
        && (!cycleStore.cycle.finished_at || (date <= cycleStore.cycle.finished_at))
      ))
    },
    getActiveCycleStoreModelByDate(date) {
      const cycleStore = self.activeCycleStoreModel;
      if (cycleStore && cycleStore.cycle.started_at <= date && (!cycleStore.cycle.finished_at || (date <= cycleStore.cycle.finished_at)))
        return cycleStore
      return undefined;
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type PondStoreType = Instance<typeof PondStoreModel>
export interface PondStore extends PondStoreType {}
type PondStoreSnapshotType = SnapshotOut<typeof PondStoreModel>
export interface PondStoreSnapshot extends PondStoreSnapshotType {}
export const createPondStoreDefaultModel = () => types.optional(PondStoreModel, {})
